<script lang="ts" setup>
import request from "@/data/api/request";
import { loader } from "@/components/loader/loader";
import { computed, onMounted, ref } from "vue";
import { toast } from "vue3-toastify";

definePage({
  meta: {
    action: "read",
    subject: "PriceConfiguration",
    navActiveLink: "pricelist-overview",
  },
});

interface PriceChangeLogRow {
  productId: string;
  sku: string;
  description: string;
  debtorId: string | null;
  debtorName: string | null;
  oldPrice: number | null;
  newPrice: number;
  startDate: string | null;
  changedAt: string;
  changedBy: string;
}

const loading = ref(false);
const rows = ref<PriceChangeLogRow[]>([]);
const debtorFilter = ref<string | null>(null);
const userFilter = ref<string | null>(null);
const skuFilter = ref("");
const dateFrom = ref("");
const dateTo = ref("");

const debtorOptions = computed(() => {
  const map = new Map<string, string>();
  for (const row of rows.value) {
    const id = String(row.debtorId ?? "").trim();
    if (!id) continue;
    const name = String(row.debtorName ?? "").trim();
    map.set(id, name ? `${id} - ${name}` : id);
  }
  return [...map.entries()]
    .map(([value, title]) => ({ value, title }))
    .sort((a, b) => a.title.localeCompare(b.title, "nl"));
});

const userOptions = computed(() => {
  const users = new Set<string>();
  for (const row of rows.value) {
    const user = String(row.changedBy ?? "").trim();
    if (user) users.add(user);
  }
  return [...users]
    .map((user) => ({ value: user, title: user }))
    .sort((a, b) => a.title.localeCompare(b.title, "nl"));
});

const filteredRows = computed(() => {
  const skuQuery = skuFilter.value.trim().toLowerCase();
  const fromDate = dateFrom.value ? new Date(`${dateFrom.value}T00:00:00`) : null;
  const toDate = dateTo.value ? new Date(`${dateTo.value}T23:59:59`) : null;

  return rows.value
    .filter((row) => {
      if (debtorFilter.value && String(row.debtorId ?? "").trim() !== debtorFilter.value) {
        return false;
      }

      if (userFilter.value && String(row.changedBy ?? "").trim() !== userFilter.value) {
        return false;
      }

      if (skuQuery) {
        const haystack = `${row.sku} ${row.description}`.toLowerCase();
        if (!haystack.includes(skuQuery)) return false;
      }

      const changedAt = new Date(row.changedAt);
      if (!Number.isNaN(changedAt.getTime())) {
        if (fromDate && changedAt < fromDate) return false;
        if (toDate && changedAt > toDate) return false;
      }

      return true;
    })
    .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
});

function formatPrice(value: number | null) {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

function formatDate(value: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function toCsvValue(value: string) {
  const escaped = value.replace(/"/g, '""');
  return `"${escaped}"`;
}

function sanitizeFilenamePart(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function buildExportFilename() {
  const parts = ["prijsmutaties"];
  const selectedDebtor = String(debtorFilter.value ?? "").trim();
  const selectedDebtorLabel = debtorOptions.value.find(
    (option) => option.value === selectedDebtor,
  )?.title;
  const selectedUser = String(userFilter.value ?? "").trim();
  const skuQuery = skuFilter.value.trim();
  const from = dateFrom.value.trim();
  const to = dateTo.value.trim();

  if (selectedDebtorLabel) {
    const debtorPart = sanitizeFilenamePart(selectedDebtorLabel);
    if (debtorPart) parts.push(`debiteur-${debtorPart}`);
  }

  if (skuQuery) {
    const skuPart = sanitizeFilenamePart(skuQuery);
    if (skuPart) parts.push(`zoek-${skuPart}`);
  }

  if (selectedUser) {
    const userPart = sanitizeFilenamePart(selectedUser);
    if (userPart) parts.push(`gebruiker-${userPart}`);
  }

  if (from) parts.push(`vanaf-${from}`);
  if (to) parts.push(`tot-${to}`);

  parts.push(new Date().toISOString().slice(0, 10));
  return `${parts.join("_")}.csv`;
}

function buildCsvContent(data: PriceChangeLogRow[]) {
  const header = [
    "SKU",
    "Omschrijving",
    "Klant/Prijslijst",
    "Oude prijs",
    "Nieuwe prijs",
    "Ingangsdatum",
    "Gebruiker",
  ];
  const lines = data.map((row) => {
    const customer = row.debtorName
      ? `${row.debtorId ?? ""} - ${row.debtorName}`.trim()
      : String(row.debtorId ?? "");
    return [
      row.sku,
      row.description,
      customer,
      row.oldPrice === null ? "" : String(row.oldPrice),
      String(row.newPrice),
      row.startDate ?? "",
      row.changedBy || "",
    ]
      .map((value) => toCsvValue(String(value ?? "")))
      .join(";");
  });

  return `${header.map(toCsvValue).join(";")}\n${lines.join("\n")}`;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildEmailTableContent(data: PriceChangeLogRow[]) {
  const header = ["SKU", "Omschrijving", "Oude prijs", "Nieuwe prijs", "Ingangsdatum"];
  const plainLines = [
    header.join("\t"),
    ...data.map((row) => [
      row.sku,
      row.description,
      formatPrice(row.oldPrice),
      formatPrice(row.newPrice),
      formatDate(row.startDate),
    ].join("\t")),
  ];

  const cellStyle = "border: 1px solid #999; padding: 6px 8px; vertical-align: top; white-space: nowrap;";
  const descriptionCellStyle = "border: 1px solid #999; padding: 6px 8px; vertical-align: top;";
  const priceCellStyle = `${cellStyle} text-align: right;`;

  const htmlRows = data.map((row) => `
    <tr>
      <td style="${cellStyle}">${escapeHtml(row.sku)}</td>
      <td style="${descriptionCellStyle}">${escapeHtml(row.description)}</td>
      <td style="${priceCellStyle}">${escapeHtml(formatPrice(row.oldPrice))}</td>
      <td style="${priceCellStyle}">${escapeHtml(formatPrice(row.newPrice))}</td>
      <td style="${cellStyle}">${escapeHtml(formatDate(row.startDate))}</td>
    </tr>`).join("");

  const html = `<table style="border-collapse: collapse; width: auto; table-layout: auto; display: inline-table;">
    <thead>
      <tr>
        ${header.map((title) => `<th style="${cellStyle} text-align: left; background-color: #f3f3f3;">${escapeHtml(title)}</th>`).join("")}
      </tr>
    </thead>
    <tbody>${htmlRows}
    </tbody>
  </table>`;

  return {
    html,
    plainText: plainLines.join("\n"),
  };
}

async function exportCsv() {
  const content = buildCsvContent(filteredRows.value);
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", buildExportFilename());
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function copyTableToClipboard() {
  const content = buildCsvContent(filteredRows.value);
  await navigator.clipboard.writeText(content);
  toast.success("Tabel gekopieerd naar klembord");
}

async function copyEmailTableToClipboard() {
  const content = buildEmailTableContent(filteredRows.value);
  if (window.ClipboardItem) {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([content.html], { type: "text/html" }),
        "text/plain": new Blob([content.plainText], { type: "text/plain" }),
      }),
    ]);
  } else {
    await navigator.clipboard.writeText(content.plainText);
  }
  toast.success("E-mail tabel gekopieerd naar klembord");
}

async function loadRows() {
  loading.value = true;
  try {
    const response = await request({
      url: "/pricelist/latest-price-changes",
      method: "GET",
    }) as { data?: PriceChangeLogRow[] };
    rows.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error: any) {
    rows.value = [];
    toast.error(error?.response?.data?.error || "Kon prijswijzigingen niet laden");
  } finally {
    loading.value = false;
    loader.stop();
  }
}

onMounted(() => {
  loadRows();
});
</script>

<template>
  <VCard>
    <VCardTitle class="d-flex flex-wrap align-center justify-space-between gap-3">
      <div>
        <div class="text-h5">Prijswijzigingen</div>
        <div class="text-body-2 text-medium-emphasis">
          Overzicht van alle verkoopprijswijzigingen per product.
        </div>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <VBtnGroup variant="flat" color="primary" divided>
          <VBtn :disabled="!filteredRows.length" @click="copyTableToClipboard">
            Acties
          </VBtn>
          <VMenu location="bottom end">
            <template #activator="{ props }">
              <VBtn
                v-bind="props"
                :disabled="!filteredRows.length"
                icon="tabler-chevron-down"
                aria-label="Kopieer opties"
              />
            </template>
            <VList>
              <VListItem @click="exportCsv">
                <VListItemTitle>Export CSV</VListItemTitle>
              </VListItem>
              <VListItem @click="copyEmailTableToClipboard">
                <VListItemTitle>Kopieer voor e-mail</VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
        </VBtnGroup>
      </div>
    </VCardTitle>

    <VCardText>
      <VRow>
        <VCol cols="12" md="4">
          <VAutocomplete
            v-model="debtorFilter"
            :items="debtorOptions"
            item-title="title"
            item-value="value"
            label="Klant / prijslijst"
            clearable
          />
        </VCol>
        <VCol cols="12" md="4">
          <VTextField
            v-model="skuFilter"
            label="Zoek op SKU of omschrijving"
            clearable
          />
        </VCol>
        <VCol cols="12" md="4">
          <VAutocomplete
            v-model="userFilter"
            :items="userOptions"
            item-title="title"
            item-value="value"
            label="Gebruiker"
            clearable
          />
        </VCol>
        <VCol cols="6" md="2">
          <VTextField v-model="dateFrom" type="date" label="Vanaf" />
        </VCol>
        <VCol cols="6" md="2">
          <VTextField v-model="dateTo" type="date" label="Tot en met" />
        </VCol>
      </VRow>

      <VDataTable
        :headers="[
          { title: 'Aangemaakt', key: 'changedAt', width: '120px' },
          { title: 'SKU', key: 'sku', width: '95px' },
          { title: 'Omschrijving', key: 'description' },
          { title: 'Klant / prijslijst', key: 'customer' },
          { title: 'Oude prijs', key: 'oldPrice', align: 'end', width: '120px' },
          { title: 'Nieuwe prijs', key: 'newPrice', align: 'end', width: '130px' },
          { title: 'Ingangsdatum', key: 'startDate', width: '140px' },
          { title: 'Gebruiker', key: 'changedBy' },
        ]"
        :items="filteredRows"
        :items-per-page="10"
        :loading="loading"
        class="mt-2"
        item-value="changedAt"
      >
        <template #no-data>
          <div class="text-center py-8 text-medium-emphasis">
            Geen prijswijzigingen gevonden voor deze selectie.
          </div>
        </template>

        <template #item.changedAt="{ item }">
          <span class="text-no-wrap">{{ formatDate(item.changedAt) }}</span>
        </template>

        <template #item.customer="{ item }">
          {{ item.debtorName ? `${item.debtorId ?? ""} - ${item.debtorName}` : item.debtorId || "-" }}
        </template>

        <template #item.oldPrice="{ item }">
          {{ formatPrice(item.oldPrice) }}
        </template>

        <template #item.newPrice="{ item }">
          {{ formatPrice(item.newPrice) }}
        </template>

        <template #item.startDate="{ item }">
          {{ formatDate(item.startDate) }}
        </template>

        <template #item.changedBy="{ item }">
          {{ item.changedBy || "-" }}
        </template>
      </VDataTable>
    </VCardText>
  </VCard>
</template>
